import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingOverlay } from '@/components/ui/LoadingOverlay'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Textarea } from '@/components/ui/Textarea'
import { VenueAmenityPicker } from '@/components/venue/VenueAmenityPicker'
import { VenueFacilityImageListEditor } from '@/components/venue/VenueFacilityImageListEditor'
import { VenueMainImageField } from '@/components/venue/VenueMainImageField'
import { VenueTagListEditor } from '@/components/venue/VenueTagListEditor'
import { VenueTriStateField } from '@/components/venue/VenueTriStateField'
import { useSaveVenueProfile } from '@/hooks/useSaveVenueProfile'
import { useVenueProfile } from '@/hooks/useVenueProfile'
import { toApiError } from '@/lib/api/errors'
import { openVenueAddressSearch, VenueAddressSearchError } from '@/lib/venue/venueAddressSearch'
import {
  clearCoordinatesOnAddressEdit,
  formatVenueContactInput,
  VENUE_PROFILE_LIMITS,
  toFieldErrorMap,
  toFormState,
  toSaveInput,
  toVenueProfileOverviewViewModel,
  validateVenueProfileForm,
  type VenueImageFormItem,
  type VenueProfileFormState,
} from '@/lib/venue/venueProfileViewModel'
import { useVenueContext } from '@/routes/venueContext'

const SAVE_NOTICE_STATIC = '저장 즉시 예약 화면과 구장 노출 정보에 반영됩니다.'

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-500">{message}</p>
}

/**
 * 구장 기본정보 편집 (요구사항 2, 설계 §2-1(d), `#867` 설계 §2).
 *
 * 🚨 운영 인증·예약 기능 상태와 무관하게 저장할 수 있다 — 이 표면은 게이트가 `ALWAYS_OPEN` 이다
 * (#594 §0, 설계 D6). 별도 접근 가드를 적용하지 않는다.
 */
export function VenueProfileEditPage() {
  const { venueId } = useVenueContext()
  const navigate = useNavigate()
  const profileQuery = useVenueProfile(venueId)
  const saveMutation = useSaveVenueProfile(venueId)
  // 🚨 `#879` — 이미지 업로드 자동 저장 전용 뮤테이션. 전역 "저장" 버튼(`saveMutation`)과 상태를
  // 분리해, 자동 저장 실패가 전역 저장 실패 배너와 섞이지 않게 한다(설계 §1 수정 방침 1).
  const imageAutoSaveMutation = useSaveVenueProfile(venueId)

  const [form, setForm] = useState<VenueProfileFormState | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [addressSearchPending, setAddressSearchPending] = useState(false)
  const [addressSearchError, setAddressSearchError] = useState<string | null>(null)
  const [imageAutoSaveError, setImageAutoSaveError] = useState<string | null>(null)

  // 조회가 도착하면 폼을 한 번 초기화한다. 이후 편집 중에는 재조회로 덮어쓰지 않는다.
  useEffect(() => {
    if (profileQuery.data && form === null) {
      setForm(toFormState(profileQuery.data))
    }
  }, [profileQuery.data, form])

  if (profileQuery.isLoading) {
    return (
      <>
        <PageHeader title="구장 기본정보 편집" />
        <LoadingState rows={4} label="구장 정보를 불러오는 중" />
      </>
    )
  }

  if (profileQuery.isError) {
    return (
      <>
        <PageHeader title="구장 기본정보 편집" />
        <ErrorState
          error={profileQuery.error}
          onRetry={() => {
            void profileQuery.refetch()
          }}
        />
      </>
    )
  }

  if (!profileQuery.data || !form) return null

  const view = profileQuery.data
  const overview = toVenueProfileOverviewViewModel(view)
  const policy = view.operation.cancellationPolicySummary

  function updateField<K extends keyof VenueProfileFormState>(key: K, value: VenueProfileFormState[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current))
  }

  /** 🚨 업로드 중인 objectURL 이 저장 요청에 섞여 들어가지 않도록 저장 버튼을 함께 잠근다(설계 §2 자율 결정). */
  const hasUploadingImage =
    form.mainImage?.status === 'uploading' || form.facilityImages.some((item) => item.status === 'uploading')
  /** 업로드 자체(개별 파일) + 업로드 직후 자동 저장 호출까지 "진행 중"으로 취급해 전체 화면을 막는다(`#879`). */
  const isImageBusy = hasUploadingImage || imageAutoSaveMutation.isPending

  /**
   * 🚨 `#879` — 이미지 업로드 성공 직후 즉시 서버에 반영한다. 사용자가 지금 입력 중인(아직 저장하지
   * 않은) 다른 필드 값(`form`)이 아니라 **최근 조회된 서버 값**(`profileQuery.data`)을 기준으로 이미지
   * 필드만 교체해 보낸다 — 그래야 미완성 편집(예: 구장명 수정 중)이 이미지 업로드 시점에 함께 조기
   * 저장되지 않는다. 실패해도 업로드된 이미지 자체는 로컬 상태(`form`)에 남아 있으므로 전역 "저장"
   * 버튼으로 재시도할 수 있다(설계 §1 수정 방침 1).
   */
  async function persistImageChange(patch: { mainImage?: VenueImageFormItem | null; facilityImages?: VenueImageFormItem[] }) {
    if (!profileQuery.data) return
    setImageAutoSaveError(null)
    const baseForm = toFormState(profileQuery.data)
    const nextForm: VenueProfileFormState = {
      ...baseForm,
      ...('mainImage' in patch ? { mainImage: patch.mainImage ?? null } : {}),
      ...('facilityImages' in patch ? { facilityImages: patch.facilityImages ?? [], facilityImagesDirty: true } : {}),
    }
    try {
      await imageAutoSaveMutation.mutateAsync(toSaveInput(nextForm))
    } catch (error) {
      setImageAutoSaveError(toApiError(error).message)
    }
  }

  async function handleAddressSearch() {
    setAddressSearchError(null)
    setAddressSearchPending(true)
    try {
      const result = await openVenueAddressSearch()
      setForm((current) =>
        current ? { ...current, address: result.address, latitude: result.latitude, longitude: result.longitude } : current
      )
    } catch (error) {
      const message = error instanceof VenueAddressSearchError ? error.message : '주소 검색에 실패했습니다.'
      setAddressSearchError(message)
    } finally {
      setAddressSearchPending(false)
    }
  }

  async function handleSubmit() {
    if (!form || hasUploadingImage) return

    const clientErrors = validateVenueProfileForm(form)
    if (clientErrors.length > 0) {
      setFieldErrors(toFieldErrorMap(clientErrors))
      return
    }
    setFieldErrors({})

    try {
      await saveMutation.mutateAsync(toSaveInput(form))
      // 🚨 요약 화면이 이 state 로 저장 완료 배너를 1회성으로 띄운다(설계 §2 "저장 완료 안내").
      navigate(`/venues/${venueId}/venue`, { state: { profileSaved: true } })
    } catch (error) {
      const apiError = toApiError(error)
      if (apiError.fieldErrors) {
        setFieldErrors(toFieldErrorMap(apiError.fieldErrors))
      }
    }
  }

  const submitError =
    saveMutation.isError && !toApiError(saveMutation.error).fieldErrors ? toApiError(saveMutation.error) : null

  return (
    <>
      {/* 🚨 `#879` — 업로드 진행 중(개별 업로드 + 업로드 직후 자동 저장)에는 전체 화면을 막는다. */}
      {isImageBusy ? <LoadingOverlay label="이미지를 업로드하는 중입니다…" /> : null}

      <PageHeader title="구장 기본정보 편집" description="사용자 구장 탭의 상세 화면에 노출되는 정보입니다." />

      <div className="mb-5 space-y-3">
        <Alert tone="info">{SAVE_NOTICE_STATIC}</Alert>
        {view.saveNotice ? <Alert tone="warning">{view.saveNotice}</Alert> : null}
        {submitError ? <Alert tone="danger">{submitError.message}</Alert> : null}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-5">
          <Card>
            <CardHeader title="기본 정보" />
            <CardBody className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-name">
                  구장명
                </label>
                <Input
                  id="venue-name"
                  value={form.name}
                  aria-invalid={Boolean(fieldErrors.name)}
                  onChange={(event) => {
                    updateField('name', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.name} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-introduction">
                  구장 소개
                </label>
                <Textarea
                  id="venue-introduction"
                  rows={3}
                  value={form.introduction}
                  maxLength={VENUE_PROFILE_LIMITS.introduction}
                  aria-invalid={Boolean(fieldErrors.introduction)}
                  onChange={(event) => {
                    updateField('introduction', event.target.value)
                  }}
                />
                <p className="mt-1 text-right text-xs text-slateish-400">
                  {form.introduction.length}/{VENUE_PROFILE_LIMITS.introduction}자
                </p>
                <FieldError message={fieldErrors.introduction} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-contact">
                    대표 연락처
                  </label>
                  <Input
                    id="venue-contact"
                    value={form.contact}
                    aria-invalid={Boolean(fieldErrors.contact)}
                    onChange={(event) => {
                      updateField('contact', formatVenueContactInput(event.target.value))
                    }}
                    placeholder="02-000-0000"
                  />
                  <FieldError message={fieldErrors.contact} />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-website">
                    웹사이트 URL
                  </label>
                  <Input
                    id="venue-website"
                    value={form.websiteUrl}
                    aria-invalid={Boolean(fieldErrors.websiteUrl)}
                    onChange={(event) => {
                      updateField('websiteUrl', event.target.value)
                    }}
                  />
                  <FieldError message={fieldErrors.websiteUrl} />
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="구장 사진" />
            <CardBody className="space-y-5">
              {/* 🚨 `#879` — 이미지 자동 저장 실패 안내. 전역 저장 실패 배너(`submitError`)와 섞이지 않게 분리한다. */}
              {imageAutoSaveError ? <Alert tone="danger">{imageAutoSaveError}</Alert> : null}
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink-700">대표 이미지</span>
                <VenueMainImageField
                  venueId={venueId}
                  image={form.mainImage}
                  onChange={(mainImage) => {
                    updateField('mainImage', mainImage)
                  }}
                  onUploaded={(mainImage) => persistImageChange({ mainImage })}
                />
              </div>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink-700">시설 이미지</span>
                <VenueFacilityImageListEditor
                  venueId={venueId}
                  images={form.facilityImages}
                  onChange={(facilityImages) => {
                    setForm((current) => (current ? { ...current, facilityImages, facilityImagesDirty: true } : current))
                  }}
                  onUploaded={(facilityImages) => persistImageChange({ facilityImages })}
                />
                <FieldError message={fieldErrors.facilityImages} />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="위치 정보" />
            <CardBody className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-address">
                  주소
                </label>
                <div className="flex gap-2">
                  <Input
                    id="venue-address"
                    value={form.address}
                    aria-invalid={Boolean(fieldErrors.address)}
                    onChange={(event) => {
                      setForm((current) => (current ? clearCoordinatesOnAddressEdit(current, event.target.value) : current))
                    }}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      void handleAddressSearch()
                    }}
                    disabled={addressSearchPending}
                  >
                    {addressSearchPending ? '검색 중…' : '주소 검색'}
                  </Button>
                </div>
                <FieldError message={fieldErrors.address} />
                {addressSearchError ? <p className="mt-1 text-xs text-danger-500">{addressSearchError}</p> : null}
                <p className="mt-1 text-xs text-slateish-500">
                  {form.latitude !== null && form.longitude !== null
                    ? `좌표 확인됨 (${form.latitude.toFixed(5)}, ${form.longitude.toFixed(5)})`
                    : '주소 검색으로 좌표를 확인해 주세요.'}
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-detail-address">
                  상세 주소
                </label>
                <Input
                  id="venue-detail-address"
                  value={form.detailAddress}
                  aria-invalid={Boolean(fieldErrors.detailAddress)}
                  onChange={(event) => {
                    updateField('detailAddress', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.detailAddress} />
              </div>
              <VenueTriStateField
                label="주차 가능 여부"
                value={form.parkingAvailable}
                onChange={(value) => {
                  updateField('parkingAvailable', value)
                }}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-parking-guide">
                  주차 안내
                </label>
                <Input
                  id="venue-parking-guide"
                  value={form.parkingGuide}
                  aria-invalid={Boolean(fieldErrors.parkingGuide)}
                  onChange={(event) => {
                    updateField('parkingGuide', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.parkingGuide} />
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="시설 정보" />
            <CardBody className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-floor-material">
                  코트 바닥 재질
                </label>
                <Input
                  id="venue-floor-material"
                  value={form.floorMaterialNotice}
                  aria-invalid={Boolean(fieldErrors.floorMaterialNotice)}
                  onChange={(event) => {
                    updateField('floorMaterialNotice', event.target.value)
                  }}
                  placeholder="예: 우레탄"
                />
                <FieldError message={fieldErrors.floorMaterialNotice} />
                <p className="mt-1 text-xs text-slateish-500">
                  코트별 상세 재질은 코트 관리에서 따로 설정합니다. 이 안내 문구는 구장 대표 안내용입니다.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <VenueTriStateField
                  label="샤워실"
                  value={form.showerAvailable}
                  onChange={(value) => {
                    updateField('showerAvailable', value)
                  }}
                />
                <VenueTriStateField
                  label="탈의실"
                  value={form.lockerRoomAvailable}
                  onChange={(value) => {
                    updateField('lockerRoomAvailable', value)
                  }}
                />
                <VenueTriStateField
                  label="스트링 작업 가능 여부"
                  value={form.stringServiceAvailable}
                  onChange={(value) => {
                    updateField('stringServiceAvailable', value)
                  }}
                />
              </div>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink-700">편의시설</span>
                <VenueAmenityPicker
                  value={form.amenities}
                  onChange={(codes) => {
                    updateField('amenities', codes)
                  }}
                />
                <FieldError message={fieldErrors.amenities} />
              </div>
              <VenueTagListEditor
                label="대여 장비"
                items={form.rentalEquipments}
                onChange={(items) => {
                  updateField('rentalEquipments', items)
                }}
              />
              <FieldError message={fieldErrors.rentalEquipments} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="운영 안내" />
            <CardBody className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-usage-guide">
                  이용 안내
                </label>
                <Textarea
                  id="venue-usage-guide"
                  rows={3}
                  value={form.usageGuide}
                  aria-invalid={Boolean(fieldErrors.usageGuide)}
                  onChange={(event) => {
                    updateField('usageGuide', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.usageGuide} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-700" htmlFor="venue-additional-notice">
                  추가 안내
                </label>
                <Textarea
                  id="venue-additional-notice"
                  rows={3}
                  value={form.additionalNotice}
                  aria-invalid={Boolean(fieldErrors.additionalNotice)}
                  onChange={(event) => {
                    updateField('additionalNotice', event.target.value)
                  }}
                />
                <FieldError message={fieldErrors.additionalNotice} />
              </div>
              <div>
                <span className="mb-1.5 block text-sm font-medium text-ink-700">
                  취소·환불 규정 요약 <span className="text-xs font-normal text-slateish-500">(읽기 전용)</span>
                </span>
                {policy.configured ? (
                  <div className="space-y-1 rounded-lg bg-slateish-50 px-3 py-2.5 text-sm text-slateish-600">
                    <p>{policy.summaryText}</p>
                    <p>{policy.cancellableUntilLabel}</p>
                    <p>{policy.feeLabel}</p>
                  </div>
                ) : (
                  <p className="rounded-lg bg-slateish-50 px-3 py-2.5 text-sm text-slateish-500">
                    아직 설정된 취소·환불 규정이 없습니다.
                  </p>
                )}
              </div>
            </CardBody>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                navigate(`/venues/${venueId}/venue`)
              }}
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={() => {
                void handleSubmit()
              }}
              disabled={saveMutation.isPending || hasUploadingImage}
            >
              {saveMutation.isPending ? '저장 중…' : hasUploadingImage ? '사진 업로드 중…' : '저장'}
            </Button>
          </div>
        </div>

        <Card className="h-fit">
          <CardHeader title="현재 구장 정보 요약" />
          <CardBody>
            <dl className="space-y-3 text-sm">
              <SummaryRow label="구장명" value={overview.name} />
              <SummaryRow label="주소" value={overview.address} />
              <SummaryRow label="대표 연락처" value={overview.contact} />
              <SummaryRow label="웹사이트" value={overview.websiteUrlText} />
              <SummaryRow label="코트 수" value={overview.courtCountText} />
              <SummaryRow label="마지막 수정일" value={overview.lastModifiedAtText} />
            </dl>
          </CardBody>
        </Card>
      </div>
    </>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-slateish-500">{label}</dt>
      <dd className="text-right font-medium text-ink-700">{value}</dd>
    </div>
  )
}
